import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react';

interface Props {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  /** Let the resident type a value that isn't on the list (e.g. an unlisted institution). */
  allowCustom?: boolean;
  invalid?: boolean;
}

// Match "10" to "(10) בנק לאומי", and ignore quotes, dashes and parentheses.
const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/["'״׳()\-–.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * One way to choose from a long list: tap the field, a bottom sheet opens with a
 * search box and tappable rows. Replaces both the native dropdown (a scroll wheel
 * on iPhone, no search) and <datalist> (suggestions hidden in the keyboard bar).
 */
export function ChoicePicker({ label, value, options, onChange, allowCustom, invalid }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = norm(query);
    return q ? options.filter((o) => norm(o).includes(q)) : options;
  }, [options, query]);
  const typed = query.trim();
  const canUseTyped = !!allowCustom && !!typed && !options.some((o) => norm(o) === norm(typed));

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    setTimeout(() => searchRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function close() {
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  }
  function choose(v: string) {
    onChange(v);
    close();
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className={
          'flex h-12 w-full items-center justify-between gap-2 rounded-xl border bg-white px-3 text-start text-[16px] outline-none transition-colors ' +
          (invalid ? 'border-red-400' : 'border-slate-200 focus:border-brand-500')
        }
      >
        <span className={'truncate ' + (value ? 'text-brand-900' : 'text-slate-400')}>
          {value || (allowCustom ? 'בחירה מהרשימה או הקלדה' : 'בחירה מהרשימה')}
        </span>
        <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={label}>
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[85vh] max-w-md flex-col rounded-t-2xl bg-white pb-[max(env(safe-area-inset-bottom),0.75rem)] shadow-2xl">
            <div className="flex items-center justify-between px-4 pt-3">
              <h2 className="text-[17px] font-bold text-brand-900">{label}</h2>
              <button
                type="button"
                onClick={close}
                aria-label="סגירה"
                className="flex h-12 w-12 items-center justify-center rounded-xl text-slate-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-4 pb-2">
              <div className="flex h-12 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 focus-within:border-brand-500">
                <Search className="h-5 w-5 shrink-0 text-slate-400" />
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={allowCustom ? 'חיפוש או הקלדת שם' : 'חיפוש'}
                  className="h-full w-full bg-transparent text-[16px] text-brand-900 outline-none"
                />
              </div>
            </div>
            <ul className="flex-1 overflow-y-auto overscroll-contain px-2" role="listbox" aria-label={label}>
              {canUseTyped && (
                <li>
                  <button
                    type="button"
                    onClick={() => choose(typed)}
                    className="flex min-h-12 w-full items-center gap-2 rounded-xl px-3 py-2 text-start text-[16px] font-medium text-brand-700 hover:bg-brand-50"
                  >
                    <Plus className="h-5 w-5 shrink-0" /> להשתמש ב: {typed}
                  </button>
                </li>
              )}
              {filtered.map((o) => (
                <li key={o} role="option" aria-selected={o === value}>
                  <button
                    type="button"
                    onClick={() => choose(o)}
                    className={
                      'flex min-h-12 w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-start text-[16px] ' +
                      (o === value ? 'bg-brand-50 font-semibold text-brand-900' : 'text-brand-900 hover:bg-slate-50')
                    }
                  >
                    <span>{o}</span>
                    {o === value && <Check className="h-5 w-5 shrink-0 text-brand-600" />}
                  </button>
                </li>
              ))}
              {filtered.length === 0 && !canUseTyped && (
                <li className="px-3 py-6 text-center text-[15px] text-slate-500">לא נמצאו תוצאות</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
